import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Settings, ChevronDown, ChevronUp } from 'lucide-react';

const COOKIE_KEY = 'lg_cookie_consent';

function getStoredPrefs() {
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && 'essential' in parsed) return parsed;
    // Legacy format (string "accepted"/"declined") — migrate
    if (raw === 'accepted') return { essential: true, analytics: true, preferences: true };
    if (raw === 'declined') return { essential: true, analytics: false, preferences: false };
    return null;
  } catch { return null; }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: true, preferences: true });

  useEffect(() => {
    const stored = getStoredPrefs();
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (choices) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify(choices));
    setVisible(false);
    setManaging(false);
  };

  const acceptAll = () => save({ essential: true, analytics: true, preferences: true });
  const declineAll = () => save({ essential: true, analytics: false, preferences: false });
  const saveCustom = () => save({ ...prefs, essential: true });

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-3 md:p-6 animate-fade-in-up pointer-events-none" data-testid="cookie-banner">
      <div className="pointer-events-auto max-w-4xl mx-auto bg-white/90 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 overflow-hidden">
        {/* Main banner */}
        <div className="p-4 md:p-6">
          <div className="mb-3">
            <p className="text-sm text-foreground font-medium mb-1">We value your privacy</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              We use cookies to enhance your experience and analyze site usage.{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setManaging(!managing)}
              className="px-4 py-2 rounded-full text-xs sm:text-sm text-muted-foreground hover:text-foreground border border-border/60 hover:border-border transition-colors flex items-center gap-1.5"
              data-testid="cookie-manage-btn"
            >
              <Settings className="w-3.5 h-3.5" />
              Manage
              {managing ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
            <button
              onClick={declineAll}
              className="px-4 py-2 rounded-full text-xs sm:text-sm text-muted-foreground hover:text-foreground border border-border/60 hover:border-border transition-colors"
              data-testid="cookie-decline-btn"
            >
              Decline All
            </button>
            <button
              onClick={acceptAll}
              className="px-4 py-2 rounded-full text-xs sm:text-sm bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
              data-testid="cookie-accept-btn"
            >
              Accept All
            </button>
          </div>
        </div>

        {/* Manage preferences panel */}
        {managing && (
          <div className="border-t border-border/30 px-5 md:px-6 py-5 space-y-4 bg-white/50" data-testid="cookie-manage-panel">
            <CookieToggle
              label="Essential Cookies"
              description="Required for the website to function. Authentication, security, and core features."
              checked={true}
              disabled={true}
              testId="cookie-toggle-essential"
            />
            <CookieToggle
              label="Analytics Cookies"
              description="Help us understand how visitors use our website so we can improve the experience."
              checked={prefs.analytics}
              onChange={(v) => setPrefs(p => ({ ...p, analytics: v }))}
              testId="cookie-toggle-analytics"
            />
            <CookieToggle
              label="Preference Cookies"
              description="Remember your settings like theme preferences and display options."
              checked={prefs.preferences}
              onChange={(v) => setPrefs(p => ({ ...p, preferences: v }))}
              testId="cookie-toggle-preferences"
            />
            <div className="pt-2 flex justify-end">
              <button
                onClick={saveCustom}
                className="px-6 py-2 rounded-full text-sm bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity"
                data-testid="cookie-save-prefs-btn"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CookieToggle({ label, description, checked, onChange, disabled = false, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange && onChange(!checked)}
        className={`mt-0.5 relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${checked ? 'bg-primary' : 'bg-muted'}`}
      >
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 mt-0.5 ${
          checked ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
        }`} />
      </button>
      <div className="flex-1">
        <p className="text-sm font-medium text-foreground">
          {label}
          {disabled && <span className="text-xs text-muted-foreground ml-2">(Always on)</span>}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
